import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  ChatState,
  Conversation,
  ConversationMessage,
  IncomingConversationMessage,
  Message,
  OverviewLatestMessage,
  TypingUser,
} from './types';

const reducers = {
  addMessage: (state: ChatState, action: PayloadAction<Message>) => {
    state.messages.push(action.payload);
  },
  setMessages: (state: ChatState, action: PayloadAction<Message[]>) => {
    state.messages = action.payload;
  },
  clearMessages: (state: ChatState) => {
    state.messages = [];
  },
  setUploadingFiles: (state: ChatState, action: PayloadAction<boolean>) => {
    state.uploadingFiles = action.payload;
  },
  setError: (state: ChatState, action: PayloadAction<string | null>) => {
    state.error = action.payload;
  },
  setSelectedConversationId: (state: ChatState, action: PayloadAction<string | null>) => {
    state.selectedConversationId = action.payload;
  },
  // Toggle the non-blocking "live updates paused" banner shown in the chat
  // section. Set true when the private overview `conversation.created`
  // subscription is refused; reset false by the panel host on mount/unmount.
  setLiveUpdatesPaused: (state: ChatState, action: PayloadAction<boolean>) => {
    state.liveUpdatesPaused = action.payload;
  },
  // WebSocket: Add incoming message from real-time events
  // Deduplicates by UUID to prevent duplicates from optimistic updates
  addIncomingMessage: (
    state: ChatState,
    // isOwnMessage is REQUIRED (IncomingConversationMessage): the compensating decrement that
    // once masked a forgotten flag is gone, so an omitted flag must fail the build rather than
    // silently regress ORI-644. Sent by the current user from another tab → still renders, but
    // never raises this viewer's unread badge. Mirrors updateConversationOverview. (ORI-644)
    action: PayloadAction<IncomingConversationMessage>,
  ) => {
    const { isOwnMessage, ...msg } = action.payload;
    const conversationId = msg.conversation_id;

    // A real-time message without a conversation id is malformed — every update
    // below keys on conversationId, so there is nothing to do.
    if (!conversationId) return;

    // Track which conversations we've already updated to avoid duplicates
    const updatedConversations = new Set<Conversation>();

    // Normalize message with stable ID
    const normalizedMessage = {
      ...msg,
      id: msg.uuid || msg.id,
    };

    state.messages.push({
      id: Date.now(),
      conversation_id: conversationId,
      content: msg.message_content,
      sender_id: parseInt(msg.sender_id, 10),
      sender_type: msg.sender_type,
      created_at: msg.created_at,
      updated_at: msg.updated_at,
      attachments: msg.attachments,
    });

    // Helper to update conversation
    const updateConv = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.id !== conversationId && conv.uuid !== conversationId) return;
      if (updatedConversations.has(conv)) return;

      if (!conv.messages) conv.messages = [];

      // Check if message already exists to prevent duplicates (by UUID or temp ID)
      const messageExists = conv.messages.some((m) => {
        // Match by UUID (primary)
        if (msg.uuid && m.uuid === msg.uuid) return true;
        // Match by ID fallback
        if (msg.id && m.id === msg.id) return true;
        // Match optimistic temp message by checking if it's a temp ID that should be replaced
        if (typeof m.id === 'string' && m.id.startsWith('temp-') && msg.uuid) {
          // This will be handled by replaceOptimisticMessage instead
          return false;
        }
        return false;
      });
      if (messageExists) return;

      conv.messages.push(normalizedMessage);
      conv.latest_message = normalizedMessage;
      conv.messages_count = conv.messages.length;
      // ORI-644: an own message echoed cross-tab must render (pushed above) but must NOT
      // bump this viewer's unread badge. After the uuid dedup, so a redelivery never counts.
      if (!isOwnMessage) {
        conv.unread_messages_count = (conv.unread_messages_count || 0) + 1;
      }
      updatedConversations.add(conv);
    };

    // Update currentConversation
    updateConv(state.currentConversation);

    // Update conversations array
    state.conversations.forEach(updateConv);

    // Update all caches
    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(updateConv);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(updateConv);
    });
  },
  addCreatedConversation: (state: ChatState, action: PayloadAction<Conversation>) => {
    const conv = action.payload;
    state.conversations.push(conv);
    state.currentConversation = conv;
  },
  updateMessageStatus: (
    state: ChatState,
    action: PayloadAction<{ messageId: number; is_read: boolean }>,
  ) => {
    const { messageId, is_read } = action.payload;
    const message = state.messages.find((m) => m.id === messageId);
    if (message) {
      message.is_read = is_read;
    }
  },
  deleteMessage: (state: ChatState, action: PayloadAction<number>) => {
    state.messages = state.messages.filter((m) => m.id !== action.payload);
  },
  addTypingUser: (state: ChatState, action: PayloadAction<TypingUser>) => {
    if (!state.typingUsers) state.typingUsers = [];
    const exists = state.typingUsers.find((u) => u.user_id === action.payload.user_id);
    if (!exists) {
      state.typingUsers.push(action.payload);
    }
  },
  removeTypingUser: (state: ChatState, action: PayloadAction<string>) => {
    if (state.typingUsers) {
      state.typingUsers = state.typingUsers.filter((u) => u.user_id !== action.payload);
    }
  },
  clearTypingUsers: (state: ChatState) => {
    state.typingUsers = [];
  },
  setCurrentConversation: (state: ChatState, action: PayloadAction<Conversation | null>) => {
    state.currentConversation = action.payload;
  },
  clearCurrentConversation: (state: ChatState) => {
    state.currentConversation = null;
  },
  prependOlderMessages: (
    state: ChatState,
    action: PayloadAction<{
      conversationId: string;
      messages: ConversationMessage[];
      nextUrl: string | null;
      hasMore: boolean;
    }>,
  ) => {
    const { conversationId, messages, nextUrl, hasMore } = action.payload;

    const normalizedMessages = messages.map((msg) => ({
      ...msg,
      id: msg.uuid || msg.id,
    }));

    const prependToConv = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.id !== conversationId && conv.uuid !== conversationId) return;

      const existingIds = new Set(conv.messages?.map((m) => m.uuid || m.id) || []);

      const newMessages = normalizedMessages.filter((msg) => !existingIds.has(msg.uuid || msg.id));

      conv.messages = [...newMessages, ...(conv.messages || [])];

      conv.messages_next_url = nextUrl;
      conv.messages_has_more = hasMore;
    };

    prependToConv(state.currentConversation);

    state.conversations.forEach(prependToConv);

    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(prependToConv);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(prependToConv);
    });
  },
  // WebSocket: Update message delivery status
  updateMessageDeliveryStatus: (
    state: ChatState,
    action: PayloadAction<{
      messageId: string;
      status: 'delivered' | 'read';
      deliveredAt?: string;
      readAt?: string;
    }>,
  ) => {
    const { messageId, status, deliveredAt, readAt } = action.payload;

    // Messages are keyed by uuid on both `id` and `uuid` once normalized, so either
    // field can carry the identifier the receipt names.
    const applyReceipt = (conv: Conversation | undefined | null) => {
      const message = conv?.messages?.find(
        (candidate) => candidate.uuid === messageId || candidate.id === messageId,
      );
      if (!message) return;

      // COM-687: a conversation sweep broadcasts delivered and read as two separate
      // queued jobs, so the delivered frame can arrive after the read one. Read
      // already implies delivered, so never walk the status backwards.
      if (status === 'delivered' && (message.is_read || message.read_at)) return;

      message.status = status;
      if (status === 'delivered') {
        message.is_delivered = true;
        message.delivered_at = deliveredAt || null;
        message.checkmark_count = 2;
        message.has_double_checkmark = true;
      }
      if (status === 'read') {
        message.is_read = true;
        message.read_at = readAt || null;
        message.checkmark_count = 2;
        message.has_double_checkmark = true;
      }
    };

    // COM-687: the caches matter — both chat panels fall back to them to pick the
    // conversation they render, so a receipt that skipped them mutated a copy nothing
    // was displaying. Same four targets as markConversationMessagesAsRead below.
    applyReceipt(state.currentConversation);
    state.conversations.forEach(applyReceipt);
    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(applyReceipt);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(applyReceipt);
    });
  },
  // WebSocket: Mark all messages in a conversation as read
  markConversationMessagesAsRead: (
    state: ChatState,
    action: PayloadAction<{
      conversationUuid: string;
      readAt: string;
      // The user who did the reading. When set, only that user's NON-own messages
      // are flipped to "read" (a receipt means the OTHER party read MY messages —
      // never mark the reader's own messages read on their behalf). Nullish keeps
      // the legacy mark-all behavior for the local reader path. (ORI-645)
      readerUserId?: string | null;
      // A receipt (the other party read my messages) must not zero MY unread badge.
      // Pass false to preserve the current unread count. (ORI-645)
      resetUnreadCount?: boolean;
    }>,
  ) => {
    const { conversationUuid, readAt, readerUserId, resetUnreadCount } = action.payload;

    // A receipt whose reader we cannot identify (readerUserId null — e.g. the paired
    // BE broadcast not yet deployed, or an unhydrated profile) must NOT fall through to
    // the legacy mark-all: with the sender's own text now in Redux, that would flip
    // their own bubbles to a "seen" nobody produced. Fail closed — ignore the receipt
    // (bubbles stay on the sent check). The local reader path always supplies an id.
    // (ORI-645, HIGH-1)
    if (resetUnreadCount === false && readerUserId == null) return;

    // Track which conversations we've already updated to avoid duplicates
    const updatedConversations = new Set<Conversation>();

    // Helper to mark conversation messages as read
    const markAsRead = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.uuid !== conversationUuid && conv.id !== conversationUuid) return;
      if (updatedConversations.has(conv)) return;

      conv.messages?.forEach((msg) => {
        // Skip the reader's own messages — a read receipt reports what the OTHER
        // party has seen, so their own bubbles must not be flipped to "read". (ORI-645)
        if (readerUserId != null && String(msg.sender_id) === String(readerUserId)) return;
        // Already read — nothing to update; avoids redundant re-marking. (#1245)
        if (msg.is_read && msg.read_at) return;
        msg.is_read = true;
        msg.read_at = readAt;
        msg.status = 'read';
        msg.checkmark_count = 2;
        msg.has_double_checkmark = true;
      });
      if (resetUnreadCount !== false) conv.unread_messages_count = 0;
      updatedConversations.add(conv);
    };

    // Update currentConversation
    markAsRead(state.currentConversation);

    // Update in conversations array
    state.conversations.forEach(markAsRead);

    // Update all caches
    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(markAsRead);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(markAsRead);
    });
  },
  // WebSocket: Update conversation overview with new message (for Chat Overview real-time updates)
  // If the conversation doesn't exist in the cache, it will be created with minimal data
  updateConversationOverview: (
    state: ChatState,
    action: PayloadAction<{
      workOrderUuid?: string;
      serviceRequestUuid?: string;
      conversationUuid: string;
      unreadCount: number;
      /**
       * Is `unreadCount` scoped to THIS viewer (a `by_audience` bucket), or the
       * viewer-agnostic flat total? Set by `resolveUnreadCount`, which is the only
       * place that can know. Governs clamp-vs-assign below; defaults to `false`
       * (assign) so a caller that cannot vouch for provenance gets the pre-ORI-665
       * behaviour rather than a silent latch. (ORI-665)
       */
      isViewerScoped?: boolean;
      /**
       * A per-user-accurate writer (`addIncomingMessage` via
       * `useConversationMessageSubscriptions`) is bound for this conversation RIGHT NOW, so
       * `unreadCount` — a viewer-agnostic flat total — must not touch an EXISTING row's count:
       * this dispatch updates the preview only. A row being CREATED still seeds from
       * `unreadCount` (nothing else has a number for it yet). Defaults to `false` (the count
       * applies), so a caller that cannot vouch for a live writer never freezes a badge.
       * Set only by `useServiceRequestMessageChannel`. (ORI-666, review F-3)
       */
      hasPerUserUnreadWriter?: boolean;
      latestMessage: OverviewLatestMessage;
      /**
       * Sent by the current user: updates an existing row, but never creates one —
       * `createMinimalConversation` types the thread from `sender_type`, so it would
       * invent a "Coordinator" row for a resident thread. It also leaves
       * `unread_messages_count` untouched, because your own message is not unread to
       * you and a send must never raise your own badge. (ORI-616)
       */
      isOwnMessage?: boolean;
    }>,
  ) => {
    const {
      workOrderUuid,
      serviceRequestUuid,
      conversationUuid,
      unreadCount,
      isViewerScoped,
      hasPerUserUnreadWriter,
      latestMessage,
      isOwnMessage,
    } = action.payload;

    const isAttachment = latestMessage.message_type === 'attachment';

    // Helper to check if conversation exists in a cache
    const findConversationInCache = (
      cache: { conversations: Conversation[]; lastFetched: number } | undefined,
    ): Conversation | undefined =>
      cache?.conversations?.find(
        (conv) => conv.uuid === conversationUuid || conv.id === conversationUuid,
      );

    // Helper to update conversation
    const updateConv = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.uuid !== conversationUuid && conv.id !== conversationUuid) return;

      // ORI-616: never apply a count off your own send. The preview below still
      // updates, so the thread shows what you just wrote — only the badge is left
      // as it was. ORI-666 (F-3): same for a flat total on a row a per-user writer
      // owns — see the SR-panel paragraph at the end of this block.
      if (!isOwnMessage && !hasPerUserUnreadWriter) {
        // ORI-665 (QA TC7). A viewer-scoped `by_audience` bucket is a LOWER bound on this
        // viewer's unread count: the backend computes `by_audience[type] = total − that
        // type's own sends` (HandleWorkOrderMessageNotifications::calculateUnreadCounts),
        // subtracting sends this viewer may not have made. For two humans sharing a role it
        // is worse than low, it is FROZEN — every message adds 1 to both terms, so the
        // bucket cannot move. Assigning it flat overwrote the per-user-accurate `+1` that
        // `addIncomingMessage` had just applied, so the badge showed the right number and
        // then reverted when the slower broadcast landed.
        //
        // So: clamp a lower bound, ASSIGN the flat total. The flat
        // `unread_count` is viewer-agnostic and includes the viewer's own sends — an UPPER
        // bound — and clamping that would latch an over-count instead of letting the next
        // broadcast re-baseline it. `isViewerScoped` carries the provenance because the two
        // are indistinguishable by value. `useConversationsOverviewChannel` is the only
        // dispatcher that sends scoped counts; `useServiceRequestMessageChannel` passes the
        // flat total and lands on `assign` — but only for a row NO per-user writer owns (see
        // the SR-panel paragraph below). (`useServiceRequestConversationsChannel` would
        // too, but it has ZERO mounts — do not cite it as a live dispatcher.)
        //
        // Clamping is safe because every dispatcher is a message-ARRIVAL event, so a
        // scoped count here can only grow. Clearing on read is `markConversationAsRead`
        // (thunk → extra-reducers) and `markConversationMessagesAsRead`. ANOTHER user's
        // inbound read receipt still deliberately declines to clear (useChatMessageHandlers
        // passes `resetUnreadCount: false`, ORI-645). The reader's OWN receipt now clears the
        // reader's other tabs, through `useConversationMessageSubscriptions` (ORI-666) — for
        // every conversation in a mounted panel's cache.
        //
        // What corrects another tab when that receipt does not reach it (no panel mounted for
        // the conversation), stated precisely because the imprecise version has already
        // misled one reviewer: there is NO timer. `RenderRightSection.tsx:127` is a
        // gate (`if (isCacheValid()) return`) inside an effect keyed on the cache object, so
        // it re-evaluates whenever the cache changes and refetches only once `lastFetched`
        // is older than CACHE_VALIDITY_MS. The practical bound is therefore: a local read,
        // reopening the panel, or the next arriving message after the 5-minute window — NOT
        // the mere passage of time. A read on another device produces a legitimately lower
        // authoritative count that the clamp discards until one of those happens.
        //
        // Do NOT delete this branch. The clamp only recovers a badge where something else
        // supplies the real number: `addIncomingMessage`, dispatched by
        // `useConversationMessageSubscriptions` (work-order panel, service-request panel and
        // vendor-portal side panel — every conversation in their cache) and by
        // `useConversationChannel` (the currently OPEN conversation only). Where neither
        // applies, this reducer is the ONLY realtime writer, so deleting it would freeze
        // those badges outright.
        //
        // SR PANEL (ORI-666, review F-3 — this was the "KNOWN GAP" here before). The SR
        // channel's `unread_count` is worse than an upper bound for one row: the BE counts
        // every unread message on the WHOLE service request, every conversation, no sender
        // exclusion (HandleServiceRequestMessageNotifications::calculateUnreadCount). Assigned
        // onto the RESIDENT row it counted the coordinator's own unread sends (AC-2) and made
        // the panel disagree with the SR list (AC-4). So `useServiceRequestMessageChannel`
        // sets `hasPerUserUnreadWriter` when the panel's `useServiceRequestConversationSubscriptions`
        // has that conversation bound, and the count is left to `addIncomingMessage` — preview
        // only here. The flat total still ASSIGNS where it is the only realtime writer: a row
        // the subscription has not bound (chat gated off by `useCanSeeChat`, or the row is being
        // lazy-created by this very event), so no badge is frozen. Residual, not closed: those
        // unbound rows still take the SR-wide number until the next fetch, and a lazy-created
        // row keeps its SR-wide seed after it becomes bound (no later SR event re-baselines it).
        // Also by design: on a bound row a colleague's read (`read_at` is shared) no longer
        // lowers the badge on the next SR event, because the count now moves only by the
        // per-user `+1` — the same as the WO panel, and consistent with ORI-645.
        // NOT a gap: the vendor-portal WORK-ORDER LISTING badge (`MessageIndicatorCell`),
        // which has its own `+delta` path in `redux/vendor-portal/work-order/reducers.ts` and
        // never reaches this reducer. The class-level fix is the per-user
        // `participant.unread-count.updated` snapshot, which has no web consumer yet — deferred
        // to ORI-813, not built in ORI-666.
        conv.unread_messages_count = isViewerScoped
          ? Math.max(conv.unread_messages_count || 0, unreadCount)
          : unreadCount;
      }
      conv.latest_message = {
        ...conv.latest_message,
        message_content: latestMessage.message_content,
        message_type: latestMessage.message_type ?? 'text',
        has_attachment: isAttachment,
        sender_id: latestMessage.sender_id || '',
        sender_type: latestMessage.sender_type,
        sender_name: latestMessage.sender_name || undefined,
        created_at: latestMessage.created_at,
        updated_at: latestMessage.created_at,
      } as ConversationMessage;
    };

    // Helper to create a minimal conversation from the event data
    const createMinimalConversation = (): Conversation => {
      // Determine conversation type based on sender_type
      const senderType = latestMessage.sender_type?.toLowerCase() || 'vendor';
      let conversationType = 'vendor';
      let isVendorConversation = false;
      let isResidentConversation = false;
      let isCoordinatorConversation = false;
      let isTechnicianConversation = false;

      if (senderType === 'resident') {
        conversationType = 'resident';
        isResidentConversation = true;
      } else if (senderType === 'coordinator') {
        conversationType = 'coordinator';
        isCoordinatorConversation = true;
      } else if (senderType === 'technician') {
        conversationType = 'technician';
        isVendorConversation = true;
        isTechnicianConversation = true;
      } else {
        conversationType = 'vendor';
        isVendorConversation = true;
      }

      return {
        id: conversationUuid,
        uuid: conversationUuid,
        work_order_uuid: workOrderUuid,
        service_request_uuid: serviceRequestUuid,
        conversation_type: conversationType,
        conversation_type_label:
          conversationType.charAt(0).toUpperCase() + conversationType.slice(1),
        is_vendor_conversation: isVendorConversation,
        is_resident_conversation: isResidentConversation,
        is_coordinator_conversation: isCoordinatorConversation,
        is_technician_conversation: isTechnicianConversation,
        created_at: latestMessage.created_at,
        updated_at: latestMessage.created_at,
        participants: [],
        messages: [],
        participants_count: 0,
        messages_count: 1,
        // ORI-665 (QA TC7): floor of 1, because this branch is reachable ONLY for a
        // colleague's message — the own-message arms above no-op creation — so at least the
        // message that created this row is unread to this viewer. A frozen `by_audience`
        // bucket arrives as 0 here, which would render a row whose own `messages_count: 1`
        // contradicts it and, because `push` leaves `lastFetched` untouched, no refetch
        // would correct it for 5 minutes. Not a clamp: there is no prior count to clamp
        // against on a row being created.
        unread_messages_count: Math.max(unreadCount, 1),
        latest_message: {
          id: undefined,
          uuid: undefined,
          sender_id: latestMessage.sender_id || '',
          sender_type: latestMessage.sender_type,
          sender_type_label: latestMessage.sender_type || '',
          sender_name: latestMessage.sender_name || undefined,
          message_content: latestMessage.message_content,
          message_type: latestMessage.message_type ?? 'text',
          message_type_label: isAttachment ? 'Attachment' : 'Text',
          is_system: false,
          has_attachment: isAttachment,
          read_at: null,
          is_read: false,
          attachments: [],
          created_at: latestMessage.created_at,
          updated_at: latestMessage.created_at,
        },
      };
    };

    // Check if conversation exists in work order cache
    if (workOrderUuid) {
      const cache = state.workOrderConversationsCache[workOrderUuid];
      const existingConv = findConversationInCache(cache);

      if (existingConv) {
        // Update existing conversation
        updateConv(existingConv);
      } else if (isOwnMessage) {
        // No row to update — leave creation to the fetch, which knows the type.
      } else if (cache?.conversations) {
        // Conversation doesn't exist - create it
        const newConversation = createMinimalConversation();
        cache.conversations.push(newConversation);
      } else {
        // Cache doesn't exist - lazy init so we don't miss real-time updates
        // (WebSocket can deliver before getWorkOrderConversation completes)
        // Use lastFetched: 0 so isCacheValid() is false and full fetch still runs when opening Chat
        const newConversation = createMinimalConversation();
        state.workOrderConversationsCache[workOrderUuid] = {
          conversations: [newConversation],
          lastFetched: 0,
        };
      }
    }

    // Check if conversation exists in service request cache
    if (serviceRequestUuid) {
      const cache = state.serviceRequestConversationsCache[serviceRequestUuid];
      const existingConv = findConversationInCache(cache);

      if (existingConv) {
        // Update existing conversation
        updateConv(existingConv);
      } else if (isOwnMessage) {
        // No row to update — see above.
      } else if (cache?.conversations) {
        // Conversation doesn't exist - create it
        const newConversation = createMinimalConversation();
        cache.conversations.push(newConversation);
      } else {
        // Cache doesn't exist - lazy init so we don't miss real-time updates
        // Use lastFetched: 0 so isCacheValid() is false and full fetch still runs when opening Chat
        const newConversation = createMinimalConversation();
        state.serviceRequestConversationsCache[serviceRequestUuid] = {
          conversations: [newConversation],
          lastFetched: 0,
        };
      }
    }

    // Update in conversations array
    state.conversations.forEach(updateConv);

    // Update currentConversation if it matches
    updateConv(state.currentConversation);
  },

  // WebSocket: Add new conversation from real-time event (conversation.created)
  addNewConversation: (
    state: ChatState,
    action: PayloadAction<{
      conversation: Conversation;
      entityType: 'serviceRequest' | 'workOrder';
      entityUuid: string;
    }>,
  ) => {
    const { conversation, entityType, entityUuid } = action.payload;
    const conversationUuid = conversation.uuid || conversation.id;

    // Helper to check if conversation already exists
    const conversationExists = (conversations: Conversation[] | undefined): boolean => {
      if (!conversations) return false;
      return conversations.some((c) => c.uuid === conversationUuid || c.id === conversationUuid);
    };

    // Add to the appropriate cache based on entity type
    if (entityType === 'workOrder') {
      const cache = state.workOrderConversationsCache[entityUuid];
      if (cache) {
        // Check if conversation already exists to prevent duplicates
        if (!conversationExists(cache.conversations)) {
          cache.conversations = [conversation, ...cache.conversations];
        }
      } else {
        // Create new cache entry if it doesn't exist
        state.workOrderConversationsCache[entityUuid] = {
          conversations: [conversation],
          lastFetched: Date.now(),
        };
      }
    } else if (entityType === 'serviceRequest') {
      const cache = state.serviceRequestConversationsCache[entityUuid];
      if (cache) {
        // Check if conversation already exists to prevent duplicates
        if (!conversationExists(cache.conversations)) {
          cache.conversations = [conversation, ...cache.conversations];
        }
      } else {
        // Create new cache entry if it doesn't exist
        state.serviceRequestConversationsCache[entityUuid] = {
          conversations: [conversation],
          lastFetched: Date.now(),
        };
      }
    }

    // Also add to conversations array if not already present
    if (!conversationExists(state.conversations)) {
      state.conversations = [conversation, ...state.conversations];
    }
  },

  addOptimisticMessage: (
    state: ChatState,
    action: PayloadAction<{
      conversationId: string;
      message: ConversationMessage;
    }>,
  ) => {
    const { conversationId, message } = action.payload;

    const addToConv = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.id !== conversationId && conv.uuid !== conversationId) return;

      if (!conv.messages) conv.messages = [];
      conv.messages.push(message);
      conv.latest_message = message;
      conv.messages_count = conv.messages.length;
    };

    // Update currentConversation
    addToConv(state.currentConversation);

    // Update conversations array
    state.conversations.forEach(addToConv);

    // Update all caches
    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(addToConv);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(addToConv);
    });
  },

  replaceOptimisticMessage: (
    state: ChatState,
    action: PayloadAction<{
      conversationId: string;
      tempId: string;
      serverMessage: ConversationMessage;
    }>,
  ) => {
    const { conversationId, tempId, serverMessage } = action.payload;

    const normalizedMessage = {
      ...serverMessage,
      id: serverMessage.uuid || serverMessage.id,
    };

    const replaceInConv = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.id !== conversationId && conv.uuid !== conversationId) return;
      if (!conv.messages) return;

      const index = conv.messages.findIndex((m) => m.id === tempId || m.uuid === tempId);
      if (index === -1) return;

      conv.messages[index] = normalizedMessage;

      // ORI-469: collapse a reverse-ordering self-echo. If the Pusher echo carrying the
      // server uuid arrived BEFORE this reconcile, `addIncomingMessage` appended it as a
      // separate row (its `temp-` dedup branch returns false), so reconciling the temp row
      // to that same uuid would leave two rows for one message (a duplicate "Delivered").
      // The authoritative uuid is known here, so drop any OTHER row already carrying it —
      // an exact-uuid match, no content heuristic, so it can never collapse two genuinely
      // distinct messages. Keep the reconciled row: it holds the merged server+client
      // attachment payload from `buildServerMessageFromUpload`.
      const serverUuid = normalizedMessage.uuid || normalizedMessage.id;
      conv.messages = conv.messages.filter(
        (m, i) => i === index || (m.uuid !== serverUuid && m.id !== serverUuid),
      );
      // Recompute counters the dropped echo mutated (latest_message, messages_count).
      // We deliberately do NOT touch unread_messages_count: with the own-message gate now in
      // addIncomingMessage the self-echo never bumped it, so a compensating decrement here
      // would wrongly erase a real unread from another sender. (ORI-644)
      conv.messages_count = conv.messages.length;
      conv.latest_message = conv.messages.at(-1) ?? normalizedMessage;
    };

    replaceInConv(state.currentConversation);

    state.conversations.forEach(replaceInConv);

    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(replaceInConv);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(replaceInConv);
    });
  },

  markOptimisticMessageFailed: (
    state: ChatState,
    action: PayloadAction<{
      conversationId: string;
      tempId: string;
    }>,
  ) => {
    const { conversationId, tempId } = action.payload;

    const markFailedInConv = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.id !== conversationId && conv.uuid !== conversationId) return;
      if (!conv.messages) return;

      const message = conv.messages.find((m) => m.id === tempId || m.uuid === tempId);
      if (message) {
        message.status = 'failed';
        message.status_label = 'Failed';
      }
    };

    markFailedInConv(state.currentConversation);

    state.conversations.forEach(markFailedInConv);

    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(markFailedInConv);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(markFailedInConv);
    });
  },

  removeOptimisticMessage: (
    state: ChatState,
    action: PayloadAction<{
      conversationId: string;
      tempId: string;
    }>,
  ) => {
    const { conversationId, tempId } = action.payload;

    const removeFromConv = (conv: Conversation | undefined | null) => {
      if (!conv) return;
      if (conv.id !== conversationId && conv.uuid !== conversationId) return;
      if (!conv.messages) return;

      conv.messages = conv.messages.filter((m) => m.id !== tempId && m.uuid !== tempId);
      conv.messages_count = conv.messages.length;

      // Update latest_message if needed
      if (conv.messages.length > 0) {
        conv.latest_message = conv.messages.at(-1);
      } else {
        conv.latest_message = undefined;
      }
    };

    removeFromConv(state.currentConversation);

    state.conversations.forEach(removeFromConv);

    Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(removeFromConv);
    });
    Object.values(state.workOrderConversationsCache).forEach((cache) => {
      cache.conversations?.forEach(removeFromConv);
    });
  },
};

export default reducers;
